"use client";

import { useEffect, useState } from "react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  if (loading) {
    return <div className="hidden md:flex gap-6">
        <div className="h-5 w-16 bg-muted/20 animate-pulse rounded" />
        <div className="h-5 w-16 bg-muted/20 animate-pulse rounded" />
        <div className="h-5 w-16 bg-muted/20 animate-pulse rounded" />
        <div className="h-5 w-16 bg-muted/20 animate-pulse rounded" />
        <div className="h-5 w-16 bg-muted/20 animate-pulse rounded" />
      </div>;
  }
  return <>
      {/* Desktop Navigation */}
      <NavigationMenu viewport={false} className="hidden md:flex">
        <NavigationMenuList className="gap-6">
          {menuItems.map(item => <NavigationMenuItem key={item.id}>
              <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="text-foreground text-sm font-medium relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-accent after:transition-all after:duration-200 hover:after:w-full hover:!bg-transparent focus:!bg-transparent">
                  {item.label}
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>)}
        </NavigationMenuList>
      </NavigationMenu>

      {/* Mobile Menu Button */}
      <Button variant="ghost" size="icon" className={`md:hidden transition-colors ${mobileMenuOpen ? "bg-[#164735]" : "hover:!bg-transparent focus:!bg-transparent"}`} onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu">
        {mobileMenuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
      </Button>

      {/* Mobile Menu */}
      {mobileMenuOpen && <div className="absolute top-full left-0 right-0 bg-primary border-t border-border md:hidden">
          <nav className="container mx-auto px-6 py-4 flex flex-col gap-4">
            {menuItems.map(item => <Link key={item.id} to={item.href || "#"} className="text-foreground text-sm font-medium hover:text-accent transition-colors" onClick={() => setMobileMenuOpen(false)}>
                {item.label}
              </Link>)}
          </nav>
        </div>}
    </>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className="sticky top-0 z-50 w-full border-b border-border transition-all duration-300 h-[80px] bg-[#154734] border-solid !shadow-[0px_12px_24px_rgba(20,21,31,0.26),_0px_6px_12px_rgba(20,21,31,0.18)] !border-[hsl(0,0%,0%)] !border-1">
      <div className="container mx-auto h-full px-6 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <img src="https://superb-silkworm.10web.cloud/wp-content/uploads/2026/02/EILH-icon.png" alt="PolyCon" className="!w-[120px] !h-[60px]" />
        </Link>

        {/* Navigation */}
        <div className="flex-1 flex !justify-end">
          <MenuProvider menu_id="32">
            <HeaderMenuConsumer />
          </MenuProvider>
        </div>

        {/* CTA Button */}
        <Button asChild className="inline-flex bg-accent text-foreground-accent hover:bg-accent-hover hover:scale-[1.03] focus:!bg-accent-hover transition-all duration-200 !text-[hsl(0,0%,100%)] !bg-[hsl(42,82%,41%)] !font-bold !font-sans !text-right ![padding:8px_16px_8px_16px] ![margin:0px_10px_0px_10px]">
          <Link to="https://forms.office.com/Pages/ResponsePage.aspx?id=2wING578lUSVNx03nMoq51J7CMuuCiFAvcVsoll2TY5UQlZPNDhSSTBURVZNTzBHVTU1UkNUNkFDNi4u" newTab>
            Register Now
          </Link>
        </Button>
      </div>
    </header>;
}